import { useEffect, useRef, useState } from 'react';
import { Button, Result, Spin, Tag, Alert } from 'antd';
import {
  ClockCircleOutlined,
  ArrowRightOutlined,
  ArrowLeftOutlined,
  CheckCircleTwoTone,
  CloseCircleTwoTone,
  ReloadOutlined
} from '@ant-design/icons';
import { useNavigate } from 'react-router-dom';
import endpoints from '../../api/endpoints';
import { getSignupState, patchSignupState } from '../../contexts/signupStore';
import { notifyError } from '../../utils/notify';

// Stage 5N-12: SEZ pending-approval screen. Shown after BOTH OTPs are
// verified for a customer who declared SEZ Unit / Developer at signup.
// Polls /api/signup/status/:id every 15 s. When the admin approves
// AND commits a tax decision, the page surfaces a "Continue to
// payment" CTA. When the admin rejects, the page shows the rejection
// reason and the support contact line.
//
// The user can leave this screen and come back later — the standard
// "Continue existing signup" flow on /signup persists the same
// pendingSignupId via signupStore + the SIGNUP_IN_PROGRESS modal.

const POLL_INTERVAL_MS = 15_000;

function formatTaxDecision(taxProfile) {
  if (!taxProfile) return null;
  if (taxProfile.sezSelectedTaxTreatment === 'sez_zero_rated') {
    return 'Zero-rated GST (0%) — SEZ supply';
  }
  if (taxProfile.sezSelectedTaxTreatment === 'regular_taxable') {
    const pct =
      taxProfile.sezSelectedTaxPercent !== null &&
      taxProfile.sezSelectedTaxPercent !== undefined
        ? `${Number(taxProfile.sezSelectedTaxPercent)}%`
        : 'standard rate';
    return `Taxable GST @ ${pct}`;
  }
  return null;
}

export default function SignupSezPendingPage() {
  const navigate = useNavigate();
  const state = getSignupState();
  const pendingSignupId = state.pendingSignupId;

  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [taxProfile, setTaxProfile] = useState(null);
  const [currentStep, setCurrentStep] = useState('sez_pending_approval');

  const pollRef = useRef(null);

  useEffect(() => {
    if (!pendingSignupId) {
      navigate('/signup', { replace: true });
    }
  }, [pendingSignupId, navigate]);

  async function refreshStatus(silent = false) {
    if (!silent) setRefreshing(true);
    try {
      const status = await endpoints.signup.status(pendingSignupId);
      if (status && status.taxProfile) setTaxProfile(status.taxProfile);
      if (status && status.currentStep) setCurrentStep(status.currentStep);
      patchSignupState({ status });
    } catch (err) {
      if (!silent) notifyError(err, { title: 'Could not refresh status' });
    } finally {
      setLoading(false);
      if (!silent) setRefreshing(false);
    }
  }

  // Initial load + 15 s polling. Polling stops once the screen
  // observes a non-pending state (approved / rejected) so we don't
  // hammer the backend after the decision is in.
  useEffect(() => {
    if (!pendingSignupId) return undefined;
    refreshStatus(true);
    pollRef.current = setInterval(() => {
      refreshStatus(true);
    }, POLL_INTERVAL_MS);
    return () => {
      if (pollRef.current) clearInterval(pollRef.current);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pendingSignupId]);

  useEffect(() => {
    const decided =
      taxProfile &&
      (taxProfile.sezPaymentApprovalStatus === 'approved' ||
        taxProfile.sezPaymentApprovalStatus === 'rejected');
    if (decided && pollRef.current) {
      clearInterval(pollRef.current);
      pollRef.current = null;
    }
  }, [taxProfile]);

  if (!pendingSignupId) return null;

  const sezStatus =
    (taxProfile && taxProfile.sezPaymentApprovalStatus) || 'pending';
  const isApproved = sezStatus === 'approved';
  const isRejected = sezStatus === 'rejected';
  const taxDecisionText = formatTaxDecision(taxProfile);

  return (
    <div className="gi-onboarding-rise bg-white rounded-2xl shadow-elevated border border-surface-200 p-5 sm:p-7 lg:p-8">
      <div className="mt-2 mb-2 flex items-start justify-between gap-3 flex-wrap">
        <div>
          <h2 className="text-2xl sm:text-3xl font-bold text-ink-900 m-0">
            SEZ approval{' '}
            {isApproved ? (
              <Tag
                color="success"
                icon={<CheckCircleTwoTone twoToneColor="#16a34a" />}
              >
                approved
              </Tag>
            ) : isRejected ? (
              <Tag
                color="error"
                icon={<CloseCircleTwoTone twoToneColor="#e11d48" />}
              >
                rejected
              </Tag>
            ) : (
              <Tag color="processing" icon={<ClockCircleOutlined />}>
                pending review
              </Tag>
            )}
          </h2>
          <p className="text-sm text-ink-500 mt-1.5 mb-0">
            SEZ Unit / Developer registrations are reviewed by our
            internal admin team before payment is enabled.
          </p>
        </div>
        <Button
          icon={<ReloadOutlined />}
          loading={refreshing}
          onClick={() => refreshStatus(false)}
        >
          Refresh status
        </Button>
      </div>

      {loading ? (
        <div className="py-12 text-center">
          <Spin />
        </div>
      ) : isApproved ? (
        <Result
          status="success"
          title="SEZ approval granted"
          subTitle={
            taxDecisionText
              ? `Tax treatment selected by admin: ${taxDecisionText}.`
              : 'You may proceed to plan selection and payment.'
          }
          extra={
            <Button
              type="primary"
              size="large"
              icon={<ArrowRightOutlined />}
              iconPosition="end"
              onClick={() => navigate('/signup/payment')}
            >
              Continue to payment
            </Button>
          }
        />
      ) : isRejected ? (
        <div className="mt-6">
          <Alert
            type="error"
            showIcon
            message="SEZ registration rejected"
            description={
              <div>
                <p className="m-0">
                  {taxProfile && taxProfile.sezPaymentRejectReason
                    ? taxProfile.sezPaymentRejectReason
                    : 'Please contact support to review your declaration.'}
                </p>
                <p className="m-0 mt-2">
                  For Support :{' '}
                  <strong>9843313491 / 9600405305</strong> or{' '}
                  <strong>support@glass-i.in</strong>
                </p>
              </div>
            }
          />
          <div className="mt-6 flex items-center justify-between flex-wrap gap-3">
            <Button onClick={() => navigate('/signup')} icon={<ArrowLeftOutlined />}>
              Back to signup
            </Button>
          </div>
        </div>
      ) : (
        <div className="mt-6">
          <Alert
            type="info"
            showIcon
            message="Your SEZ registration is pending internal approval"
            description={
              <div>
                <p className="m-0">
                  Our team is verifying your SEZ Unit / Developer
                  declaration and the LOA / approval number you
                  provided. You can proceed to payment after approval.
                </p>
                <p className="m-0 mt-2">
                  We poll for status updates every 15 seconds. You may
                  also close this page and come back later — your
                  signup is held for 7 days, and the{' '}
                  <strong>Continue existing signup</strong> button on
                  the signup page will bring you back here.
                </p>
              </div>
            }
          />
          <div className="mt-6 flex items-center justify-between flex-wrap gap-3">
            <Button onClick={() => navigate('/signup')} icon={<ArrowLeftOutlined />}>
              Back to signup
            </Button>
            <span className="text-xs text-ink-500">
              Auto-refreshing every 15 s
            </span>
          </div>
        </div>
      )}
    </div>
  );
}
